var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useRef, useId, } from 'react';
import { useButton } from '@react-aria/button';
import { useComboBox } from '@react-aria/combobox';
import { useFilter } from '@react-aria/i18n';
import { useComboBoxState } from '@react-stately/combobox';
import { isSmallTextFieldSizes } from '../../utils/input';
import { applyMargins, tvMerge } from '../../utils/tailwind';
import { ErrorMessage } from '../Form/ErrorMessage';
import { IconButton } from '../IconButton/IconButton';
import { DEFAULT_SIZE, DEFAULT_VARIANT } from '../TextField/TextField.constants';
import { affix, input, inputCentralizer, inputLabel, inputWrapper } from '../TextField/styles';
import { Typography } from '../Typography/Typography';
import { TYPOGRAPHY_VARIANTS } from '../Typography/styles';
import { ListBox } from './ListBox';
import { Popover } from './Popover';
export const SelectAutocomplete = (props) => {
    const { m, ml, mr, mb, mt, mx, my } = props, selectProps = __rest(props, ["m", "ml", "mr", "mb", "mt", "mx", "my"]);
    const { contains } = useFilter({ sensitivity: 'base' });
    const ariaLabel = selectProps['aria-label'] || selectProps.placeholder;
    // Create state based on the incoming props
    const state = useComboBoxState(Object.assign(Object.assign({}, props), { defaultFilter: contains }));
    // Get props for child elements from useSelect
    const ref = useRef(null);
    const buttonRef = useRef(null);
    const inputRef = useRef(null);
    const listBoxRef = useRef(null);
    const popoverRef = useRef(null);
    const { buttonProps: triggerProps, inputProps, listBoxProps, } = useComboBox(Object.assign(Object.assign(Object.assign({}, props), (ariaLabel && { 'aria-label': ariaLabel })), { inputRef,
        buttonRef,
        listBoxRef,
        popoverRef }), state);
    const { buttonProps } = useButton(triggerProps, ref);
    const size = selectProps.size || DEFAULT_SIZE;
    const variant = selectProps.variant || DEFAULT_VARIANT;
    const labelOption = selectProps.labelOption || 'default';
    const isShrunk = ((state.isFocused || state.selectedKey !== null) && labelOption !== 'nonshrink') || labelOption === 'sticky';
    const isHidden = isSmallTextFieldSizes(size) || (!isShrunk && state.inputValue !== '');
    const hasLabel = !!selectProps.label;
    const hasPrefix = !!selectProps.prefix;
    const hasSuffix = !!selectProps.suffix;
    const isLoading = !!selectProps.isLoading;
    // Fix a bug that caused element to blur when input is focused and button was clicked
    buttonProps.onClick = () => {
        if (state.isOpen) {
            state.setFocused(true);
        }
    };
    const errorId = useId();
    return (_jsxs("div", { className: tvMerge([applyMargins({ m, ml, mr, mb, mt, mx, my }), selectProps.className]), onBlur: () => {
            state.setFocused(false);
        }, children: [_jsxs("div", { className: inputWrapper({
                    size,
                    withStroke: variant === 'primary',
                    error: selectProps.error,
                    isFocused: state.isFocused,
                    disabled: selectProps.isDisabled,
                }), children: [hasPrefix && _jsx("div", { className: affix({ variant: 'prefix', size }), children: selectProps.prefix }), _jsxs("div", { className: inputCentralizer({
                            size,
                            center: !hasLabel || labelOption === 'nonshrink',
                        }), children: [hasLabel && (_jsx(Typography, { variant: isShrunk ? 'footnote' : 'body', display: isHidden ? 'hidden' : undefined, htmlFor: selectProps.id, component: "label", weight: isShrunk ? 'medium' : 'normal', className: inputLabel({
                                    size,
                                    shrunk: isShrunk,
                                    disabled: selectProps.isDisabled,
                                    error: selectProps.error,
                                }), children: selectProps.label })), _jsx("input", Object.assign({}, (selectProps.name && { name: selectProps.name }), { "aria-invalid": !!selectProps.error, "aria-describedby": selectProps.error ? errorId : undefined, ref: inputRef, disabled: selectProps.isDisabled, placeholder: selectProps.placeholder }, inputProps, { className: input({
                                    placeholderVisible: !hasLabel,
                                    className: TYPOGRAPHY_VARIANTS.body,
                                }) }))] }), hasSuffix ? (_jsx("div", { className: affix({ variant: 'suffix', size }), children: selectProps.suffix })) : (_jsx("div", { className: affix(), children: _jsx(IconButton, Object.assign({ ref: ref }, buttonProps, { disabled: selectProps.isDisabled, name: "chevron-down", variant: "text", size: 24, mr: isSmallTextFieldSizes(size) ? 8 : 16 })) }))] }), _jsxs("div", { className: "relative", children: [state.isOpen && (_jsx(Popover, { popoverRef: popoverRef, isOpen: state.isOpen, onClose: () => {
                            var _a;
                            state.close();
                            state.setFocused(false);
                            (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.blur();
                        }, children: _jsx(ListBox, Object.assign({}, listBoxProps, { state: state, isLoading: isLoading, footer: props.footer })) })), _jsx(ErrorMessage, { id: errorId, error: selectProps.error, errorMessage: selectProps.errorMessage })] })] }));
};
